import { useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { SCHOOL_FEATURE_MAP, featuresByGroup } from '@/lib/schoolFeatures'
import type { FeatureFacet } from '@/types/publicSchool'

interface FeatureFiltersProps {
  facets: FeatureFacet[]
  selected: string[]
  onChange: (keys: string[]) => void
}

function countOf(facets: FeatureFacet[], key: string) {
  return facets.find((facet) => facet.key === key)?.count ?? 0
}

/**
 * Aparência de um chip de filtro. Exportada porque a fila de categorias
 * (CategoryFilters) usa a mesma: as duas aparecem no header, uma em cima da
 * outra em alguns tamanhos, e um chip meio pixel diferente do outro lê como
 * bug.
 *
 * `bg-white` explícito: os chips vivem no header, que é translúcido, e o
 * conteúdo que passa por baixo dele é cinza.
 */
export function chipClass(
  active: boolean,
  disabled: boolean,
  /** Sobrescreve o que precisar — o `cn` (tailwind-merge) resolve o conflito.
   *  É por aqui que o segundo nível do filtro fica menor que o primeiro. */
  className?: string,
) {
  return cn(
    // focus-visible: anel de foco para teclado — os chips são <button> crus,
    // fora do shadcn, e sem isto o Tab passa por eles sem feedback visível.
    'inline-flex shrink-0 items-center gap-2 rounded-full border bg-white px-4 py-2 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-gray-200 text-gray-700 hover:border-primary/40',
    disabled && 'cursor-not-allowed opacity-40 hover:border-gray-200',
    className,
  )
}

function Chip({
  featureKey,
  count,
  active,
  onToggle,
}: {
  featureKey: string
  count: number
  active: boolean
  onToggle: () => void
}) {
  const feature = SCHOOL_FEATURE_MAP[featureKey]
  if (!feature) return null

  const Icon = feature.icon
  // Contagem zero fica visível mas desabilitada: sumir faria o conjunto de filtros
  // dançar a cada clique e o usuário perderia a referência.
  const disabled = count === 0 && !active

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={active}
      aria-label={feature.label}
      disabled={disabled}
      onClick={onToggle}
      className={chipClass(active, disabled)}
    >
      <Icon aria-hidden="true" className="h-4 w-4" />
      {feature.label}
    </button>
  )
}

const toggle = (key: string, list: string[]) =>
  list.includes(key) ? list.filter((item) => item !== key) : [...list, key]

/**
 * Botão + dialog com o catálogo completo, agrupado por tipo.
 *
 * Os selos existem SÓ aqui dentro. Antes eles também formavam uma fila de chips
 * no header, e a primeira coisa que o Explore oferecia era "aula para
 * iniciante" — refinamento antes da pergunta que importa. A fila de fora agora
 * é a de nicho (CategoryFilters); os selos são o passo seguinte, atrás deste
 * botão.
 */
export function FeatureFilterDialog({
  facets,
  selected,
  onChange,
  className,
}: FeatureFiltersProps & { className?: string }) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState<string[]>(selected)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setPending(selected)
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'shrink-0 rounded-full border-gray-300 bg-white',
            // Com filtro ativo o botão assume a cor da marca, igual aos chips:
            // é o mesmo estado ("tem filtro aqui"), e a contagem sozinha some no
            // meio da fila.
            selected.length > 0 && 'border-primary text-primary',
            className,
          )}
        >
          <SlidersHorizontal aria-hidden="true" className="mr-2 h-4 w-4" />
          Filtros
          {selected.length > 0 && (
            <Badge className="ml-2 h-5 px-1.5">
              {selected.length}
              <span className="sr-only">
                {selected.length === 1 ? 'filtro ativo' : 'filtros ativos'}
              </span>
            </Badge>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-[620px]">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>Filtros</DialogTitle>
          <DialogDescription>
            A escola precisa ter todos os selos marcados para aparecer na lista.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {(
            [
              ['level', 'Níveis de aula'],
              ['amenity', 'Facilidades'],
            ] as const
          ).map(([group, title]) => (
            <section key={group} className="space-y-3">
              <h4 className="text-sm font-semibold">{title}</h4>
              <div className="flex flex-wrap gap-2">
                {featuresByGroup(group).map((feature) => (
                  <Chip
                    key={feature.key}
                    featureKey={feature.key}
                    count={countOf(facets, feature.key)}
                    active={pending.includes(feature.key)}
                    onToggle={() =>
                      setPending((list) => toggle(feature.key, list))
                    }
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <DialogFooter className="items-center border-t px-6 py-4 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            className="px-0 underline underline-offset-4 hover:bg-transparent"
            onClick={() => setPending([])}
          >
            Limpar tudo
          </Button>
          <Button
            type="button"
            onClick={() => {
              onChange(pending)
              setOpen(false)
            }}
          >
            Ver escolas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
